import { useMemo, useState, useEffect, useCallback } from 'react';
import type { HTMLAttributes, ReactNode } from 'react';
import styled from '@emotion/styled';
import { theme } from '../styles/theme';
import { Card } from '../styles/components';
import { SearchableSelect } from './SearchableSelect';
import { haushaltsstelleAvailabilityKey } from '../utils/deriveFilterTrees';
import type { FilterState, FilterOptions, HierarchicalFilterOption, FilterOption } from '../types';
import type { FilterAvailability } from '../utils/deriveFilterTrees';
import { categoryLabel, einzelplanLabel, lookupLabel, useT, type LookupFamily } from '../i18n';

// Phone breakpoint for the bottom-sheet treatment. A phone rotated to
// landscape keeps a width >600px but a very short height, so `max-width: 600px`
// alone switched the sheet off on rotation and the panel reverted to the
// full-width inline accordion that swamped the short viewport. Also treating
// any short landscape viewport as a phone keeps the sheet across rotation;
// tablets in landscape (height ≥768px) are excluded by the max-height clause.
const PHONE = '@media (max-width: 600px), (max-height: 600px) and (orientation: landscape)';

// In-card header row — Figma anchors "Filter" + "zurücksetzen" at the top of
// the card with a ~29 px gap to the first section header. --space-8 (24 px)
// plus the accordion item's own 6 px top-padding lands close.
const FilterHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
  margin: 0 0 var(--space-8) 0;

  /* Inside the mobile sheet this is the sticky title bar. */
  ${PHONE} {
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 1;
    margin: 0;
    padding: var(--space-4) var(--space-5);
    background: var(--color-bg-surface);
    border-bottom: 1px solid var(--color-bg-muted);
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

// The header "zurücksetzen" link is desktop-only; on mobile reset lives in the
// sticky sheet footer instead (alongside "Fertig").
const DesktopOnly = styled.span`
  display: contents;
  ${PHONE} { display: none; }
`;

// Figma "Filter" caption — sans, default text colour, regular weight (not
// the previous uppercase muted small-caps). Bumped one step up the sans
// scale (sans-m → sans-l) for a clearer panel heading.
const SectionTitle = styled.h3`
  font-family: var(--sans-stack);
  font-size: 1.25rem;
  font-weight: 400;
  color: var(--color-text-default);
  line-height: 1.2;
  margin: 0;
`;

const FilterCard = styled(Card)`
  /* Figma card inset is ~18 px top / ~19.5 px sides. With the accordion
     adding its own per-section padding inside, we want the card padding
     itself moderate. Pick --space-5 (15 px) horizontally + --space-4 (12 px)
     vertically so the total visual rhythm matches Figma's 319 px-tall card. */
  padding: var(--space-4) var(--space-5);
  position: relative;
  /* Lift the whole filter sidebar above the chart area so dropdown popups
     opened inside an accordion section can overlay chart axes etc. */
  z-index: 1000;

  /* Card component applies .tile--elevation-low (--shadow-s, very faint).
     Figma's "Tile Shadow" is more present — closest shared equivalent is
     --shadow-m. Without this the card visually blends into the page
     background since both use --color-bg-surface. */
  box-shadow: var(--shadow-m);

  /* Phone: becomes a bottom sheet summoned by the Filter button (see
     MobileTriggerButton), so the page opens on the data. Pinned to the bottom,
     slides up on [data-open], off-viewport + inert when closed. */
  ${PHONE} {
    position: fixed;
    inset: auto 0 0 0;
    margin: 0;
    padding: 0;
    max-height: 88vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    border-radius: var(--radius-l) var(--radius-l) 0 0;
    z-index: 1100;
    box-shadow: 0 -6px 24px rgba(24, 28, 68, 0.22);
    transform: translateY(100%);
    transition: transform 0.28s ease;
    pointer-events: none;

    &[data-open='true'] {
      transform: translateY(0);
      pointer-events: auto;
    }
  }
`;

// ── Mobile bottom-sheet chrome ──────────────────────────────────────────────
// Rendered at every width but display:none above 600px, so desktop/tablet keep
// the inline accordion card untouched.

const MobileTriggerBar = styled.div`
  display: none;
  ${PHONE} {
    display: block;
  }
`;

const MobileTriggerButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  width: 100%;
  padding: var(--space-3) var(--space-4);
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-default);
  background: var(--color-bg-surface);
  border: 1px solid var(--color-bg-muted);
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-s);
  cursor: pointer;

  svg { width: 18px; height: 18px; }
`;

const FilterBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--primary-color);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
`;

const Scrim = styled.div`
  display: none;
  ${PHONE} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 1099;
    background: rgba(24, 28, 68, 0.4);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.28s ease;

    &[data-open='true'] {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const SheetCloseButton = styled.button`
  display: none;
  ${PHONE} {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    background: none;
    border-radius: var(--radius-m);
    font-size: 1.5rem;
    line-height: 1;
    color: var(--color-text-secondary);
    cursor: pointer;

    &:hover { background: var(--color-bg-muted); }
  }
`;

const SheetFooter = styled.div<{ dropdownOpen: boolean }>`
  display: none;
  ${PHONE} {
    display: flex;
    gap: ${theme.spacing.sm};
    position: sticky;
    bottom: 0;
    z-index: 50;
    padding: var(--space-4) var(--space-5);
    background: var(--color-bg-surface);
    border-top: 1px solid var(--color-bg-muted);

    /* While a dropdown is open we hide this sticky bar outright. Relying on
       z-index alone (footer 50 below an open dropdown's container at 100) is
       correct per spec but iOS Safari's sticky-stacking quirk still painted
       this opaque bar over the open suggestion list, hiding options behind
       "Zurücksetzen" / "Fertig" — most visibly for the topmost dropdown, whose
       long list reaches furthest down into the footer. visibility:hidden keeps
       the footer's layout box (no reflow of the sheet) while removing both its
       paint and its pointer-event capture, so options underneath stay fully
       visible and tappable. The footer returns the moment the dropdown closes. */
    visibility: ${props => (props.dropdownOpen ? 'hidden' : 'visible')};
  }
`;

const SheetResetButton = styled.button`
  flex: 0 0 auto;
  padding: var(--space-3) var(--space-5);
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  background: none;
  border: 1px solid var(--color-bg-muted);
  border-radius: var(--radius-m);
  cursor: pointer;
`;

const SheetDoneButton = styled.button`
  flex: 1;
  padding: var(--space-3) var(--space-5);
  font-family: var(--sans-stack);
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: var(--primary-color);
  border: none;
  border-radius: var(--radius-m);
  cursor: pointer;
`;

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

// Wrapper around the shared `.accordion` family. Several local overrides:
//   1. The shared `.accordion__item { overflow: hidden }` clips the height-
//      collapse transition. Once an item is open, override to overflow:
//      visible so absolutely-positioned dropdown popups can escape the
//      section bounds. Same override on `__content`.
//   2. Give the (div) header a full-width clickable row with pointer cursor.
//   3. Tighten all the shared vertical paddings — the shared accordion uses
//      6 px on item top/bottom, 6 px more on the header, and another 6 px on
//      the content. That stacks to ~24 px of chrome per section, far too
//      tall for a 319 px-tall Figma sidebar. We collapse it to ~3 px each.
//   4. Shrink the per-level dropdown labels (rendered by SearchableSelect as
//      <label> elements inside .accordion__content) to the Figma "micro
//      label" size of 10 px.
const AccordionWrapper = styled.div`
  white-space: normal;

  /* Doubled class selector raises specificity above the shared
     .accordion__item { overflow: hidden } so dropdown popups inside an
     open section can extend past the section's bottom edge. */
  .accordion__item.accordion__item--open {
    overflow: visible;
  }
  .accordion__item.accordion__item--open > .accordion__content {
    overflow: visible;
  }

  /* Item-level padding is the only vertical chrome on a collapsed section.
     --space-4 (12 px) on each side gives clear separation between adjacent
     collapsed headers (12 + 16 + 12 + 1 ≈ 41 px per section, vs Figma's
     ~30 px — we're choosing the more spacious side because the Figma
     compact frame felt cramped in practice). */
  .accordion__item {
    padding-top: var(--space-4);
    padding-bottom: var(--space-4);
  }

  .accordion__header {
    align-items: center;
    border: none;
    background: transparent;
    color: inherit;
    font: inherit;
    width: 100%;
    text-align: left;
    padding: 0;
    cursor: pointer;
  }

  .accordion__header[data-has-filter='true'] {
    font-weight: 600;
  }

  /* Zero out the content padding when the section is collapsed — the
     shared rule sets padding-top on .accordion__content unconditionally,
     and at height: 0 the lower part of that padding still shows
     children (overflow: hidden clips at the padding-box edge, not the
     content edge). When the section opens, restore Figma's ~21 px gap
     from header bottom to the first micro-label. */
  .accordion__content {
    padding-top: 0;
    padding-bottom: 0;
  }
  .accordion__item.accordion__item--open > .accordion__content {
    padding-top: var(--space-7);
    padding-bottom: var(--space-3);
  }

  /* SearchableSelect renders its level label as a <label> at the top of
     each dropdown. Bumped from Figma's 10 px micro-label to sans-m (1rem =
     13px at the shared root): at the production 13px root the 0.625rem
     micro-label rendered ~8px, too small to read. */
  .accordion__content label {
    font-size: 1rem;
    padding: 0 0.6em;
  }

  /* FilterCard goes edge-to-edge as a sheet on phones, so the accordion supplies
     its own side gutters and a little breathing room above the sticky footer. */
  ${PHONE} {
    padding: var(--space-3) var(--space-5) var(--space-4);
  }
`;

// Stack of per-level dropdowns. Figma measures ~13 px between one pill's
// bottom and the next pill's micro-label, so --space-4 (12 px) lands close
// without going over.
const LevelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
`;

const ClearButtonBase = styled.div`
  &.button:hover { background: transparent; color: ${theme.colors.error}; }
`;
const ClearButton = (props: HTMLAttributes<HTMLDivElement>) => (
  <ClearButtonBase
    {...props}
    className={`button button--ghost button--size-s ${props.className ?? ''}`.trim()}
  />
);

// ── Tree helpers ──────────────────────────────────────────────────────────
// The filter trees encode hierarchy as nested `children`. Per-level dropdowns
// need flat option lists (the parent level) or children of a chosen parent
// (the dependent levels).

function toOption(n: HierarchicalFilterOption): FilterOption {
  return { value: n.value, label: n.label, count: n.count };
}

function flatLevel(
  tree: HierarchicalFilterOption[] | undefined,
  targetDepth: number,
  currentDepth = 1,
): HierarchicalFilterOption[] {
  if (!tree) return [];
  if (currentDepth === targetDepth) return tree;
  return tree.flatMap(n =>
    n.children ? flatLevel(n.children, targetDepth, currentDepth + 1) : [],
  );
}

// Like flatLevel, but carries each leaf's parent node label so callers can
// disambiguate sub-labels that repeat under different parents.
function flatLevelWithParent(
  tree: HierarchicalFilterOption[] | undefined,
  targetDepth: number,
  currentDepth = 1,
  parentLabel: string | null = null,
): Array<{ node: HierarchicalFilterOption; parentLabel: string | null }> {
  if (!tree) return [];
  if (currentDepth === targetDepth) return tree.map(node => ({ node, parentLabel }));
  return tree.flatMap(n =>
    n.children ? flatLevelWithParent(n.children, targetDepth, currentDepth + 1, n.label) : [],
  );
}

function findNode(
  tree: HierarchicalFilterOption[] | undefined,
  value: string,
): HierarchicalFilterOption | null {
  if (!tree) return null;
  for (const node of tree) {
    if (node.value === value) return node;
    if (node.children) {
      const found = findNode(node.children, value);
      if (found) return found;
    }
  }
  return null;
}

// Options for a dependent level: children of `parentValue` if set, otherwise
// the flat list of nodes at `fallbackDepth` (lets users pick at the deeper
// level without first narrowing the parent).
function dependentOptions(
  tree: HierarchicalFilterOption[] | undefined,
  parentValue: string | null,
  fallbackDepth: number,
): FilterOption[] {
  if (parentValue) {
    const parent = findNode(tree, parentValue);
    return (parent?.children ?? []).map(toOption);
  }
  return flatLevel(tree, fallbackDepth).map(toOption);
}

export type TreeChangeHandler = (value: string | null, depth: number | null) => void;

interface FilterPanelProps {
  filterOptions: FilterOptions;
  filters: FilterState;
  onPolitikfeldChange: TreeChangeHandler;
  onHaushaltsstelleChange: TreeChangeHandler;
  onGruppierungChange: TreeChangeHandler;
  onFunktionChange: TreeChangeHandler;
  onClearAll: () => void;
  hasActiveFilters: boolean;
  availability?: FilterAvailability | null;
}

type SectionKey = 'politikfeld' | 'haushaltsstelle' | 'gruppierung' | 'funktion';

interface AccordionSectionProps {
  label: string;
  isOpen: boolean;
  hasFilter: boolean;
  onToggle: () => void;
  children: ReactNode;
}

function AccordionSection({ label, isOpen, hasFilter, onToggle, children }: AccordionSectionProps) {
  return (
    <div className={`accordion__item ${isOpen ? 'accordion__item--open' : ''}`.trim()}>
      <div
        className="accordion__header"
        aria-expanded={isOpen}
        data-has-filter={hasFilter}
        onClick={onToggle}
      >
        <span>{label}</span>
      </div>
      <div className="accordion__content">
        {children}
      </div>
    </div>
  );
}

export function FilterPanel({
  filterOptions,
  filters,
  onPolitikfeldChange,
  onHaushaltsstelleChange,
  onGruppierungChange,
  onFunktionChange,
  onClearAll,
  hasActiveFilters,
  availability,
}: FilterPanelProps) {
  const m = useT();

  // Per-section active-filter detection — drives the bold-header hint.
  const sectionHasFilter: Record<SectionKey, boolean> = {
    politikfeld: !!(filters.expenditureType || filters.subExpType),
    haushaltsstelle: !!(filters.einzelplan || filters.kapitel),
    gruppierung: !!(filters.hauptgruppe || filters.obergruppe || filters.gruppierung),
    funktion: !!(filters.hauptfunktion || filters.oberfunktion || filters.funktion),
  };

  // Sections start collapsed — the sidebar reads as a compact table of
  // contents and the user expands only the dimensions they care about.
  const [openSections, setOpenSections] = useState<Record<SectionKey, boolean>>({
    politikfeld: false,
    haushaltsstelle: false,
    gruppierung: false,
    funktion: false,
  });

  // Mobile bottom-sheet open state (ignored above 600px, where the panel is
  // inline and the trigger is hidden).
  const [sheetOpen, setSheetOpen] = useState(false);

  // Count of currently-open dropdowns inside the sheet. Drives hiding the
  // sticky footer so it can't overlay an open suggestion list (see SheetFooter).
  // The callback must be stable so SearchableSelect's open/close effect doesn't
  // re-fire every render; the functional updater keeps the count balanced.
  const [openDropdownCount, setOpenDropdownCount] = useState(0);
  const handleDropdownOpenChange = useCallback((open: boolean) => {
    setOpenDropdownCount(c => Math.max(0, c + (open ? 1 : -1)));
  }, []);

  useEffect(() => {
    if (!sheetOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSheetOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [sheetOpen]);

  // Badge count = number of filter dimensions with an active selection.
  const activeCount = Object.values(sectionHasFilter).filter(Boolean).length;

  const toggle = (key: SectionKey) =>
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  // ── Per-level option lists ─────────────────────────────────────────────
  // Top-level (parent) options are always the flat set of nodes at depth 1.
  // Dependent levels narrow to the picked parent's children, or fall back to
  // the full flat level when no parent is picked.

  // Politikfeld, Einzelplan, Kapitel, Gruppierung and Funktion labels are data
  // values, translated for display only; option values (the filter keys) stay
  // German.
  const translated = (family: LookupFamily) => (o: FilterOption): FilterOption => ({ ...o, label: lookupLabel(family, o.label) });
  const politikfeldOberOptions = useMemo(
    () => flatLevel(filterOptions.politikfeldTree, 1).map(n => ({ ...toOption(n), label: categoryLabel(n.label) })),
    [filterOptions.politikfeldTree],
  );
  const politikfeldUnterOptions = useMemo(() => {
    // With an Oberkategorie picked, children already share that parent — show
    // the plain sub-label. Without one, the flattened level-2 list mixes sub-types
    // from every parent and the same sub-label (e.g. "Bildung") can appear under
    // multiple expenditure types, so append the parent to disambiguate.
    if (filters.expenditureType) {
      return dependentOptions(filterOptions.politikfeldTree, filters.expenditureType, 2)
        .map(o => ({ ...o, label: categoryLabel(o.label) }));
    }
    return flatLevelWithParent(filterOptions.politikfeldTree, 2).map(({ node, parentLabel }) => ({
      value: node.value,
      label: parentLabel ? `${categoryLabel(node.label)} — ${categoryLabel(parentLabel)}` : categoryLabel(node.label),
      count: node.count,
    }));
  }, [filterOptions.politikfeldTree, filters.expenditureType]);

  const einzelplanOptions = useMemo(
    () => flatLevel(filterOptions.haushaltsstelleTree, 1).map(n => ({ ...toOption(n), label: einzelplanLabel(n.label) })),
    [filterOptions.haushaltsstelleTree],
  );
  const kapitelOptions = useMemo(
    () => dependentOptions(filterOptions.haushaltsstelleTree, filters.einzelplan, 2).map(translated('kapitel')),
    [filterOptions.haushaltsstelleTree, filters.einzelplan],
  );
  // Sondervermögen: the "Anlage …" chapters, served pre-filtered. Deliberately
  // NOT narrowed by the picked Einzelplan — the point of this control is to
  // jump straight to a fund wherever its chapter lives.
  const sondervermoegenOptions = useMemo(
    () => (filterOptions.sondervermoegen ?? []).map(translated('kapitel')),
    [filterOptions.sondervermoegen],
  );
  // Both this and the Kapitel select write `filters.kapitel`, so guard the
  // displayed value: without it, picking an ordinary chapter would leave this
  // control showing a stale clear button for a value it doesn't offer.
  const sondervermoegenValue =
    filters.kapitel && sondervermoegenOptions.some(o => o.value === filters.kapitel)
      ? filters.kapitel
      : null;

  const hauptgruppeOptions = useMemo(
    () => flatLevel(filterOptions.gruppierungTree, 1).map(toOption).map(translated('gruppierung')),
    [filterOptions.gruppierungTree],
  );
  const obergruppeOptions = useMemo(
    () => dependentOptions(filterOptions.gruppierungTree, filters.hauptgruppe, 2).map(translated('gruppierung')),
    [filterOptions.gruppierungTree, filters.hauptgruppe],
  );
  const gruppierungOptions = useMemo(
    () => dependentOptions(filterOptions.gruppierungTree, filters.obergruppe ?? filters.hauptgruppe, 3).map(translated('gruppierung')),
    [filterOptions.gruppierungTree, filters.obergruppe, filters.hauptgruppe],
  );

  const hauptfunktionOptions = useMemo(
    () => flatLevel(filterOptions.funktionTree, 1).map(toOption).map(translated('funktion')),
    [filterOptions.funktionTree],
  );
  const oberfunktionOptions = useMemo(
    () => dependentOptions(filterOptions.funktionTree, filters.hauptfunktion, 2).map(translated('funktion')),
    [filterOptions.funktionTree, filters.hauptfunktion],
  );
  const funktionOptions = useMemo(
    () => dependentOptions(filterOptions.funktionTree, filters.oberfunktion ?? filters.hauptfunktion, 3).map(translated('funktion')),
    [filterOptions.funktionTree, filters.oberfunktion, filters.hauptfunktion],
  );

  // ── Availability annotation ────────────────────────────────────────────
  // The per-dimension counts narrow options based on the *other* current
  // filters. When a value isn't reachable, we drop its count to 0 (the
  // SearchableSelect treats count purely as a display hint). When the matrix
  // hasn't loaded yet, we leave counts untouched.
  const annotateAvailability = (
    options: FilterOption[],
    lookup: ((value: string) => number | null) | null,
  ): FilterOption[] => {
    if (!lookup) return options;
    return options.map(o => {
      const count = lookup(o.value);
      return count === null ? { ...o, count: 0 } : { ...o, count };
    });
  };

  const politikfeldLookup = useMemo(
    () => availability ? (v: string) => availability.politikfeld.get(v) ?? null : null,
    [availability],
  );
  const haushaltsstelleLookup = useMemo(
    () => availability
      ? (v: string) => availability.haushaltsstelle.get(haushaltsstelleAvailabilityKey(v)) ?? null
      : null,
    [availability],
  );
  const gruppierungLookup = useMemo(
    () => availability ? (v: string) => availability.gruppierung.get(v) ?? null : null,
    [availability],
  );
  const funktionLookup = useMemo(
    () => availability ? (v: string) => availability.funktion.get(v) ?? null : null,
    [availability],
  );

  return (
    <>
      <MobileTriggerBar>
        <MobileTriggerButton
          type="button"
          onClick={() => setSheetOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={sheetOpen}
        >
          <FilterIcon />
          {m.filterPanel.title}
          {activeCount > 0 && <FilterBadge>{activeCount}</FilterBadge>}
        </MobileTriggerButton>
      </MobileTriggerBar>

      <Scrim data-open={sheetOpen} onClick={() => setSheetOpen(false)} aria-hidden="true" />

      <FilterCard data-open={sheetOpen}>
      <FilterHeader>
        <SectionTitle>{m.filterPanel.title}</SectionTitle>
        <HeaderRight>
          {hasActiveFilters && (
            <DesktopOnly>
              <ClearButton onClick={onClearAll}>{m.filterPanel.reset}</ClearButton>
            </DesktopOnly>
          )}
          <SheetCloseButton type="button" onClick={() => setSheetOpen(false)} aria-label={m.filterPanel.closeSheet}>
            ×
          </SheetCloseButton>
        </HeaderRight>
      </FilterHeader>
      <AccordionWrapper className="accordion accordion--size-s">

          <AccordionSection
            label={m.filterPanel.politikfeld}
            isOpen={openSections.politikfeld}
            hasFilter={sectionHasFilter.politikfeld}
            onToggle={() => toggle('politikfeld')}
          >
            <LevelStack>
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.oberkategorie}
                placeholder={m.filterPanel.allOberkategorien}
                options={annotateAvailability(politikfeldOberOptions, politikfeldLookup)}
                value={filters.expenditureType}
                onChange={v => onPolitikfeldChange(v, v ? 1 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.unterkategorie}
                placeholder={m.filterPanel.allUnterkategorien}
                options={annotateAvailability(politikfeldUnterOptions, politikfeldLookup)}
                value={filters.subExpType}
                onChange={v => onPolitikfeldChange(v, v ? 2 : null)}
              />
            </LevelStack>
          </AccordionSection>

          <AccordionSection
            label={m.filterPanel.haushaltsstelle}
            isOpen={openSections.haushaltsstelle}
            hasFilter={sectionHasFilter.haushaltsstelle}
            onToggle={() => toggle('haushaltsstelle')}
          >
            <LevelStack>
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.einzelplan}
                placeholder={m.filterPanel.allEinzelplaene}
                options={annotateAvailability(einzelplanOptions, haushaltsstelleLookup)}
                value={filters.einzelplan}
                onChange={v => onHaushaltsstelleChange(v, v ? 1 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.kapitel}
                placeholder={m.filterPanel.allKapitel}
                options={annotateAvailability(kapitelOptions, haushaltsstelleLookup)}
                value={filters.kapitel}
                onChange={v => onHaushaltsstelleChange(v, v ? 2 : null)}
              />
              {sondervermoegenOptions.length > 0 && (
                <SearchableSelect
                  onOpenChange={handleDropdownOpenChange}
                  label={m.filterPanel.sondervermoegen}
                  placeholder={m.filterPanel.allSondervermoegen}
                  options={annotateAvailability(sondervermoegenOptions, haushaltsstelleLookup)}
                  value={sondervermoegenValue}
                  onChange={v => onHaushaltsstelleChange(v, v ? 2 : null)}
                />
              )}
            </LevelStack>
          </AccordionSection>

          <AccordionSection
            label={m.filterPanel.gruppierungSection}
            isOpen={openSections.gruppierung}
            hasFilter={sectionHasFilter.gruppierung}
            onToggle={() => toggle('gruppierung')}
          >
            <LevelStack>
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.hauptgruppe}
                placeholder={m.filterPanel.allHauptgruppen}
                options={annotateAvailability(hauptgruppeOptions, gruppierungLookup)}
                value={filters.hauptgruppe?.includes(',') ? null : filters.hauptgruppe}
                onChange={v => onGruppierungChange(v, v ? 1 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.obergruppe}
                placeholder={m.filterPanel.allObergruppen}
                options={annotateAvailability(obergruppeOptions, gruppierungLookup)}
                value={filters.obergruppe}
                onChange={v => onGruppierungChange(v, v ? 2 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.gruppierung}
                placeholder={m.filterPanel.allGruppierungen}
                options={annotateAvailability(gruppierungOptions, gruppierungLookup)}
                value={filters.gruppierung}
                onChange={v => onGruppierungChange(v, v ? 3 : null)}
              />
            </LevelStack>
          </AccordionSection>

          <AccordionSection
            label={m.filterPanel.funktionSection}
            isOpen={openSections.funktion}
            hasFilter={sectionHasFilter.funktion}
            onToggle={() => toggle('funktion')}
          >
            <LevelStack>
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.hauptfunktion}
                placeholder={m.filterPanel.allHauptfunktionen}
                options={annotateAvailability(hauptfunktionOptions, funktionLookup)}
                value={filters.hauptfunktion}
                onChange={v => onFunktionChange(v, v ? 1 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.oberfunktion}
                placeholder={m.filterPanel.allOberfunktionen}
                options={annotateAvailability(oberfunktionOptions, funktionLookup)}
                value={filters.oberfunktion}
                onChange={v => onFunktionChange(v, v ? 2 : null)}
              />
              <SearchableSelect
                onOpenChange={handleDropdownOpenChange}
                label={m.filterPanel.funktion}
                placeholder={m.filterPanel.allFunktionen}
                options={annotateAvailability(funktionOptions, funktionLookup)}
                value={filters.funktion}
                onChange={v => onFunktionChange(v, v ? 3 : null)}
              />
            </LevelStack>
          </AccordionSection>

      </AccordionWrapper>
      <SheetFooter dropdownOpen={openDropdownCount > 0}>
        <SheetResetButton type="button" onClick={onClearAll}>{m.common.reset}</SheetResetButton>
        <SheetDoneButton type="button" onClick={() => setSheetOpen(false)}>{m.common.done}</SheetDoneButton>
      </SheetFooter>
      </FilterCard>
    </>
  );
}
